import { supabase } from '../lib/supabase';
import puterAI from '../lib/puter';
import confidenceScoring from './confidenceScoring';
import { validateResponseUnit, convertLegacyToResponseUnit } from './responseUnitUtils';
import { responseUnitService } from './responseUnitService';

/**
 * Intelligent Response Retrieval Service
 * Retrieves responses with confidence scoring and AI fallback
 */

const CONFIDENCE_THRESHOLD = 75; // Minimum confidence to use cached response
const AI_GENERATION_COOLDOWN = 5000; // 5 seconds between AI generations

let lastAIGeneration = 0;

/**
 * Get response with confidence-based retrieval and AI fallback
 * @param {Object} request - Request context
 * @param {string} request.type - Response type (objection, script, hint, etc.)
 * @param {string} request.query - Search query or objection text
 * @param {string} request.persona - Persona type (CONSULTATIVE, CHALLENGER, etc.)
 * @param {string} request.stage - Sales stage (discovery, negotiation, etc.)
 * @param {string} request.industry - Industry (tech, healthcare, etc.)
 * @param {string} request.objectionType - Objection type (price, timing, etc.)
 * @param {number} request.minConfidence - Override default confidence threshold
 * @returns {Promise<Object>} - Response unit with confidence metadata
 */
export const getResponseWithConfidence = async (request) => {
  const {
    type,
    query,
    persona,
    stage,
    industry,
    objectionType,
    minConfidence = CONFIDENCE_THRESHOLD
  } = request;

  try {
    // Step 1: Search for existing response units
    const { data: candidates, error: searchError } = await supabase
      .from('response_units')
      .select('*')
      .eq('type', type)
      .eq('is_approved', true)
      .order('quality_score', { ascending: false })
      .limit(20); // Get top 20 to score

    if (searchError) throw searchError;

    if (!candidates || candidates.length === 0) {
      // No candidates found - generate fresh response
      return await generateAndCacheResponse(request);
    }

    // Step 2: Score candidates by confidence
    const context = {
      persona,
      stage,
      industry,
      objectionType
    };

    const scoredCandidates = confidenceScoring.rankResponsesByConfidence(
      candidates,
      context,
      0 // Don't filter yet, we want to see all scores
    );

    // Step 3: Check if best match meets threshold
    const bestMatch = scoredCandidates[0];
    
    if (bestMatch && bestMatch.confidence.score >= minConfidence) {
      // High confidence match found - use cached response
      await incrementUsageCount(bestMatch.id);
      
      return {
        ...bestMatch,
        source: 'cache',
        cacheHit: true,
        confidence: bestMatch.confidence,
        alternativesCount: scoredCandidates.length - 1
      };
    }

    // Step 4: Confidence too low - generate fresh response with AI
    console.log(`Confidence too low (${bestMatch?.confidence?.score || 0}/${minConfidence}). Generating fresh response...`);
    
    const freshResponse = await generateAndCacheResponse(request);
    
    return {
      ...freshResponse,
      source: 'ai_generated',
      cacheHit: false,
      alternativesCandidates: scoredCandidates.slice(0, 3), // Include top 3 alternatives for comparison
      generationReason: `Best match confidence ${bestMatch?.confidence?.score || 0} below threshold ${minConfidence}`
    };

  } catch (error) {
    console.error('Error in getResponseWithConfidence:', error);
    throw error;
  }
};

/**
 * Generate fresh response using AI and cache it
 * @param {Object} request - Request context
 * @returns {Promise<Object>} - Newly generated and cached response unit
 */
const generateAndCacheResponse = async (request) => {
  const { type, query, persona, stage, industry, objectionType } = request;

  // Rate limiting for AI generation
  const now = Date.now();
  if (now - lastAIGeneration < AI_GENERATION_COOLDOWN) {
    const waitTime = AI_GENERATION_COOLDOWN - (now - lastAIGeneration);
    await new Promise(resolve => setTimeout(resolve, waitTime));
  }
  lastAIGeneration = Date.now();

  try {
    // Build AI prompt based on type
    const prompt = buildAIPrompt(request);
    
    // Generate response using Puter AI
    const aiResponse = await puterAI.generateContent(prompt, {
      temperature: 0.7,
      model: 'claude-sonnet-4-5'
    });

    // Parse AI response
    let content = '';
    if (typeof aiResponse === 'string') {
      content = aiResponse;
    } else if (aiResponse?.content?.[0]?.text) {
      content = aiResponse.content[0].text;
    } else {
      throw new Error('Invalid AI response format');
    }

    // Normalize to Response Unit format
    const responseUnit = normalizeAIResponse(content, request);

    // Generate coverage map
    const coverageMap = confidenceScoring.generateCoverageMap(responseUnit);
    responseUnit.coverage_map = coverageMap;

    // Validate
    const validation = validateResponseUnit(responseUnit);
    if (validation.score < 70) {
      console.warn('Low quality AI response:', validation);
    }

    // Save to database
    const saved = await responseUnitService.createResponseUnit(responseUnit);

    return {
      ...saved,
      confidence: {
        score: 100, // Freshly generated = perfect match
        breakdown: { persona: 25, stage: 25, industry: 25, objection: 25 },
        matchQuality: 'excellent'
      }
    };

  } catch (error) {
    console.error('Error generating AI response:', error);
    throw error;
  }
};

/**
 * Build AI prompt based on request type and context
 * @param {Object} request - Request context
 * @returns {string} - Formatted prompt for AI
 */
const buildAIPrompt = (request) => {
  const { type, query, persona, stage, industry, objectionType } = request;

  let systemPrompt = `You are a sales coaching expert. Generate a response using the APVN framework:
- Acknowledge: Empathetically acknowledge the concern
- Probe: Ask clarifying questions to understand deeper
- Value: Present value proposition with evidence
- Next-Step: Clear call-to-action to move forward

Format your response as a structured dialogue following this framework.`;

  let userPrompt = '';

  if (type === 'objection') {
    userPrompt = `Context:
- Persona: ${persona || 'Consultative'}
- Stage: ${stage || 'Discovery'}
- Industry: ${industry || 'General'}
- Objection Type: ${objectionType || 'General'}

Objection: "${query}"

Provide a professional sales response following the APVN framework.`;
  } else if (type === 'script') {
    userPrompt = `Create a ${stage || 'discovery'} sales script for ${industry || 'general'} industry using ${persona || 'consultative'} persona.

Scenario: ${query}

Follow the APVN framework.`;
  } else if (type === 'hint') {
    userPrompt = `Provide a quick coaching hint for this situation:

${query}

Context: ${persona || 'Consultative'} persona, ${stage || 'discovery'} stage, ${industry || 'general'} industry.

Keep it concise (2-3 sentences) and actionable.`;
  }

  return `${systemPrompt}\n\n${userPrompt}`;
};

/**
 * Normalize AI-generated text into Response Unit structure
 * @param {string} content - Raw AI response text
 * @param {Object} request - Original request context
 * @returns {Object} - Normalized Response Unit
 */
const normalizeAIResponse = (content, request) => {
  const { type, query, persona, stage, industry, objectionType } = request;

  // Simple parsing - try to extract APVN sections
  const phases = parseAPVNPhases(content);

  return {
    id: `ai_generated_${Date.now()}`,
    type,
    title: query.substring(0, 100),
    phases,
    metadata: {
      persona: persona || 'CONSULTATIVE',
      difficulty: 'intermediate',
      industry: industry || 'general',
      sales_stage: stage || 'discovery',
      objection_type: objectionType,
      aiGenerated: true,
      generatedAt: new Date().toISOString()
    },
    tags: [type, persona, industry, stage].filter(Boolean),
    quality_score: 85, // Default quality for AI-generated content
    is_approved: false // Needs review before full approval
  };
};

/**
 * Parse APVN phases from AI response text
 * @param {string} content - AI response text
 * @returns {Object} - APVN phases structure
 */
const parseAPVNPhases = (content) => {
  // Simple regex-based parsing
  const acknowledgeMatch = content.match(/Acknowledge:?\s*(.+?)(?=Probe:|Probe\s|$)/is);
  const probeMatch = content.match(/Probe:?\s*(.+?)(?=Value:|Value\s|$)/is);
  const valueMatch = content.match(/Value:?\s*(.+?)(?=Next[- ]?Step:|Next Step\s|$)/is);
  const nextStepMatch = content.match(/Next[- ]?Step:?\s*(.+?)$/is);

  return {
    acknowledge: {
      statement: acknowledgeMatch?.[1]?.trim() || content.substring(0, 200),
      tone: 'empathetic'
    },
    probe: {
      questions: probeMatch?.[1]?.split('\n').filter(q => q.trim()).slice(0, 3) || [],
      intent: 'Understand customer needs'
    },
    value: {
      statement: valueMatch?.[1]?.trim() || '',
      framework: 'direct',
      keyPoints: []
    },
    nextStep: {
      cta: nextStepMatch?.[1]?.trim() || 'What would you like to discuss next?',
      ctaType: 'question'
    }
  };
};

/**
 * Increment usage count for a response unit
 * @param {string} responseUnitId - Response unit ID
 */
const incrementUsageCount = async (responseUnitId) => {
  try {
    await supabase.rpc('increment_response_unit_usage_rpc', {
      response_unit_id: responseUnitId
    });
  } catch (error) {
    console.error('Error incrementing usage count:', error);
  }
};

export default {
  getResponseWithConfidence
};
